import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, MapPin, Users, IndianRupee, CheckCircle2, Globe } from "lucide-react";
import api from "@/lib/api";
import { optimizeImageUrl } from "@/lib/imageUtils";
import SEOHead from "@/components/SEOHead";
import ServiceForm from "@/components/ServiceForm";

export default function VacancyDetail() {
  const { slug } = useParams();
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/vacancies/${slug}`).then((res) => setVacancy(res.data)).catch(() => setVacancy(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={32} /></div>;
  if (!vacancy) return <div className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">Vacancy not found.</div>;

  const schema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": vacancy.title,
    "description": vacancy.description,
    "datePosted": vacancy.created_at || undefined,
    "validThrough": vacancy.deadline || undefined,
    "employmentType": vacancy.employment_type || "FULL_TIME",
    "hiringOrganization": { "@type": "Organization", "name": "Gulfudaan" },
    "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressCountry": vacancy.country_of_work } },
  };

  return (
    <div data-testid="vacancy-detail-page" className="bg-white pb-16 sm:pb-24">
      <SEOHead pageKey={`vacancy-${slug}`} schema={[schema]} />
      <div className="relative h-[26vh] sm:h-[38vh] min-h-[200px]">
        {vacancy.banner_image ? (
          <img src={optimizeImageUrl(vacancy.banner_image, 1400)} alt={vacancy.title} fetchPriority="high" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#1A1A1A]" />
        )}
        <div className="absolute inset-0 bg-[#1A1A1A]/55 flex flex-col items-center justify-center text-center px-4">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)] flex items-center gap-1.5"><MapPin size={12} /> {vacancy.country_of_work}</span>
          <h1 data-testid="vacancy-detail-title" className="font-heading text-3xl sm:text-4xl text-white mt-2">{vacancy.title}</h1>
          {vacancy.trade && <span className="text-white/90 text-sm mt-2">{vacancy.trade}</span>}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB]">
              <p className="text-xs text-[var(--brand-secondary)] flex items-center gap-1"><IndianRupee size={12} /> Salary</p>
              <p data-testid="vacancy-salary" className="text-sm font-medium text-[#1A1A1A] mt-1">{vacancy.salary || "As per interview"}</p>
            </div>
            <div className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB]">
              <p className="text-xs text-[var(--brand-secondary)] flex items-center gap-1"><Users size={12} /> Positions</p>
              <p data-testid="vacancy-quantity" className="text-sm font-medium text-[#1A1A1A] mt-1">{vacancy.quantity}</p>
            </div>
            <div className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB]">
              <p className="text-xs text-[var(--brand-secondary)] flex items-center gap-1"><Globe size={12} /> Apply From</p>
              <p data-testid="vacancy-available-countries" className="text-sm font-medium text-[#1A1A1A] mt-1">{(vacancy.available_countries || ["India"]).join(", ")}</p>
            </div>
          </div>

          {vacancy.description && (
            <div>
              <h2 className="font-heading text-2xl text-[#1A1A1A] mb-3">Job Details</h2>
              <p data-testid="vacancy-description" className="text-[var(--brand-secondary)] leading-relaxed whitespace-pre-line">{vacancy.description}</p>
            </div>
          )}

          {vacancy.requirements?.length > 0 && (
            <div data-testid="vacancy-requirements">
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Requirements</h3>
              <ul className="space-y-2">
                {vacancy.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--brand-secondary)]"><CheckCircle2 size={15} className="text-green-600 shrink-0 mt-0.5" /> {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB] mb-4">
              <p className="font-heading text-xl text-[#1A1A1A]">Apply for this Job</p>
              <p className="text-xs text-[var(--brand-secondary)] mt-1">No application fee. Our team will contact you on WhatsApp.</p>
            </div>
            <ServiceForm service={vacancy} entityType="vacancy" />
          </div>
        </div>
      </div>
    </div>
  );
}
